<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import type { DecorationPageTemplateBinding, DecorationTemplateUpgradePreview, DecorationTemplateVersion } from '@xjlv2/api-client'
import { RrModal, RrButton, RrSelect, RrTag } from '@xjlv2/ui'
import { collectTemplateStructuralChanges, higherTemplateVersions, previewMatchesTarget } from '../utils/business-template-dialog-state'

type Resolution = 'USE_TARGET' | 'KEEP_TENANT'
type Conflict = { path: string; reason: string; tenantValue?: unknown; targetValue?: unknown; slotCode: string }
const props = defineProps<{
  open: boolean
  binding: DecorationPageTemplateBinding
  versions: DecorationTemplateVersion[]
  preview?: DecorationTemplateUpgradePreview | null
  busy?: boolean
}>()
const emit = defineEmits<{
  close: []
  preview: [version: string, resolutionsJson: string]
  apply: [version: string, resolutionsJson: string]
}>()
const state = reactive({ targetVersion: '', resolutions: {} as Record<string, Resolution> })

watch(() => props.open, (open) => {
  if (!open) return
  state.targetVersion = availableVersions.value[0]?.templateVersion || ''
  state.resolutions = {}
})

watch(() => state.targetVersion, () => {
  state.resolutions = {}
})

const availableVersions = computed(() => higherTemplateVersions(props.versions, props.binding.draftTemplateVersion || ''))
const versionOptions = computed(() => availableVersions.value.map(v => ({
  value: v.templateVersion,
  label: `${v.templateVersion} · ${v.templateName}`
})))
const sourceVersion = computed(() => props.versions.find(item => item.templateVersion === props.binding.draftTemplateVersion))
const targetVersion = computed(() => props.versions.find(item => item.templateVersion === state.targetVersion))
const currentPreview = computed(() => props.preview?.targetVersion === state.targetVersion ? props.preview : null)
const conflicts = computed<Conflict[]>(() => {
  try {
    const values = JSON.parse(currentPreview.value?.conflictsJson || '[]') as Array<Omit<Conflict, 'slotCode'>>
    return values.map(item => ({ ...item, slotCode: item.path.match(/^\/slots\/([^/]+)/)?.[1] || 'page' }))
  } catch { return [] }
})
const conflictGroups = computed(() => Object.entries(conflicts.value.reduce<Record<string, Conflict[]>>((groups, conflict) => {
  ;(groups[conflict.slotCode] ??= []).push(conflict)
  return groups
}, {})))
const structuralChanges = computed(() => collectTemplateStructuralChanges(sourceVersion.value, targetVersion.value))
const resolutionsJson = computed(() => JSON.stringify(state.resolutions))

const resolutionOptions = [
  { value: 'USE_TARGET', label: '使用新模板' },
  { value: 'KEEP_TENANT', label: '保留当前设置' }
]

function requestPreview() {
  if (state.targetVersion) emit('preview', state.targetVersion, resolutionsJson.value)
}

function applyUpgrade() {
  if (previewMatchesTarget(currentPreview.value, state.targetVersion)) {
    emit('apply', state.targetVersion, resolutionsJson.value)
  }
}

function close() {
  emit('close')
}
</script>

<template>
  <RrModal
    :open="open"
    title="升级业务模板"
    description="Business Template"
    :width="680"
    :confirm-loading="busy"
    :ok-button-props="{ disabled: busy || !previewMatchesTarget(currentPreview, state.targetVersion) }"
    ok-text="应用到草稿"
    cancel-text="取消"
    @ok="applyUpgrade"
    @cancel="close"
    @update:open="!$event && close()"
  >
    <div class="ren-upgrade-dialog__body">
      <div class="ren-upgrade-dialog__version-banner">
        <span class="ren-upgrade-dialog__version-badge">{{ currentPreview?.fromVersion || binding.draftTemplateVersion }}</span>
        <span class="ren-upgrade-dialog__arrow">→</span>
        <span class="ren-upgrade-dialog__version-badge is-target">{{ state.targetVersion || '请选择版本' }}</span>
      </div>

      <div class="ren-upgrade-dialog__field">
        <label>目标版本</label>
        <div class="ren-upgrade-dialog__target-row">
          <RrSelect
            v-model:value="state.targetVersion"
            :options="versionOptions"
            :disabled="busy"
            placeholder="请选择目标版本"
          />
          <RrButton
            type="primary"
            :disabled="busy || !state.targetVersion"
            @click="requestPreview"
          >
            预览升级
          </RrButton>
        </div>
      </div>

      <section v-if="currentPreview" class="ren-upgrade-dialog__section">
        <h4>保留的租户修改</h4>
        <pre>{{ currentPreview.retainedOverridesJson }}</pre>
      </section>

      <section v-if="structuralChanges.length" class="ren-upgrade-dialog__section">
        <h4>结构变化</h4>
        <article v-for="change in structuralChanges" :key="change.path" class="ren-upgrade-dialog__change-item">
          <div>
            <strong>{{ change.slotCode }}</strong>
            <code>{{ change.path }}</code>
          </div>
          <RrTag type="info">{{ change.changeType }}</RrTag>
        </article>
      </section>

      <section v-if="conflicts.length" class="ren-upgrade-dialog__section">
        <h4>升级冲突</h4>
        <div v-for="([slotCode, group]) in conflictGroups" :key="slotCode" class="ren-upgrade-dialog__conflict-group">
          <h5>{{ slotCode }}</h5>
          <article v-for="conflict in group" :key="conflict.path" class="ren-upgrade-dialog__conflict-item">
            <div>
              <code>{{ conflict.path }}</code>
              <span>{{ conflict.reason }}</span>
            </div>
            <RrSelect
              v-model:value="state.resolutions[conflict.path]"
              :options="resolutionOptions"
              placeholder="选择处理方式"
              @update:value="requestPreview"
            />
          </article>
        </div>
      </section>
      <p v-else-if="currentPreview" class="ren-upgrade-dialog__ready">没有未解决冲突，可以应用到草稿。</p>
    </div>
  </RrModal>
</template>

<style scoped>
.ren-upgrade-dialog__body {
  display: grid;
  gap: 16px;
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 4px;
}

.ren-upgrade-dialog__version-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  background: var(--rr-color-fill-quaternary, #F8FAFC);
  border-radius: var(--rr-radius-container, 6px);
}

.ren-upgrade-dialog__version-badge {
  font-size: 16px;
  font-weight: 700;
  color: var(--rr-color-text-strong);
}

.ren-upgrade-dialog__version-badge.is-target {
  color: var(--rr-color-primary, #5A46E0);
}

.ren-upgrade-dialog__arrow {
  color: var(--rr-color-text-muted);
  font-size: 18px;
}

.ren-upgrade-dialog__field {
  display: grid;
  gap: 6px;
}

.ren-upgrade-dialog__field label {
  font-size: 13px;
  font-weight: 500;
  color: var(--rr-color-text);
}

.ren-upgrade-dialog__target-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
}

.ren-upgrade-dialog__section {
  display: grid;
  gap: 8px;
}

.ren-upgrade-dialog__section h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.ren-upgrade-dialog__section pre {
  max-height: 150px;
  overflow: auto;
  padding: 10px;
  margin: 0;
  background: var(--rr-color-fill-quaternary, #F8FAFC);
  border: 1px solid var(--rr-color-border, #E2E8F0);
  border-radius: var(--rr-radius-container, 6px);
  font-size: 12px;
}

.ren-upgrade-dialog__change-item,
.ren-upgrade-dialog__conflict-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--rr-color-border, #E2E8F0);
}

.ren-upgrade-dialog__change-item div,
.ren-upgrade-dialog__conflict-item div {
  display: grid;
  gap: 2px;
}

.ren-upgrade-dialog__conflict-group h5 {
  margin: 8px 0 4px;
  font-size: 13px;
  color: var(--rr-color-text-muted);
}

.ren-upgrade-dialog__ready {
  margin: 0;
  color: var(--rr-color-success, #10B981);
  font-size: 13px;
  font-weight: 500;
}
</style>
